import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ImpContainer, ImpSpacer, ImpText } from '../..';
import { colors } from '../../DesignSystem/colors';
import { ANIMATION_DURATION, MaxWidth, } from '../../DesignSystem/design-system';
import { TYPOGRAPHY, fonts } from '../../DesignSystem/typography';
const getBorderColor = (showError, showWarning) => {
    if (showError)
        return colors.border.borderError;
    if (showWarning)
        return colors.border.borderWarning;
    return colors.border.borderVariant;
};
const getBackgroundColor = (showError, showWarning) => {
    if (showError)
        return colors.surface.surfaceError;
    if (showWarning)
        return colors.surface.surfaceWarning;
    return colors.surface.surfaceMid;
};
const getFocusBorderColor = (showError, showWarning) => {
    if (showError)
        return colors.border.borderErrorFocus;
    if (showWarning)
        return colors.border.borderWarningFocus;
    return colors.border.borderFocus;
};
const getCursor = (isDisabled, isSelect) => {
    if (isDisabled) {
        return 'not-allowed';
    }
    else if (isSelect) {
        return 'pointer';
    }
    else {
        return 'auto';
    }
};
const getInputStyle = () => {
    return `
    font-size: 16px;
    line-height: 22px;
    font-family: ${fonts.regular};
  `;
};
const Input = styled.input `
  box-sizing: border-box;
  height: 56px;
  width: 100%;
  font-family: ${fonts.regular};
  font-feature-settings: 'ss02' on, 'ss09' on, 'zero' on;
  margin: 0;
  padding: 24px 0 8px 12px;
  position: relative;
  border-radius: 8px;
  border: 1px solid;
  border-color: ${({ showError, showWarning }) => getBorderColor(showError, showWarning)};
  ${getInputStyle()}
  color: ${colors.content.onSurface};
  transition: ${ANIMATION_DURATION} ease all;
  background-color: ${({ showError, showWarning }) => getBackgroundColor(showError, showWarning)};
  cursor: ${({ isDisabled, isSelect }) => getCursor(isDisabled, isSelect)};
  :focus {
    outline: none !important;
    border-color: ${({ showError, showWarning }) => getFocusBorderColor(showError, showWarning)};
  }
  ${({ isDropdownShown }) => isDropdownShown &&
    `
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  `}

  &:-webkit-autofill {
    background-color: ${colors.content.onBackgroundInverse};
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type='number'] {
    -moz-appearance: textfield;
  }
`;
const getLabelColor = (showError, showWarning) => {
    if (showError)
        return colors.content.onSurfaceError;
    if (showWarning)
        return colors.content.onSurfaceWarning;
    return colors.content.onSurfaceVariant;
};
const getSmallLabelStyles = () => {
    return `
    ${TYPOGRAPHY.body3.desktop}
    padding-top: 8px;
  `;
};
function getTrailingIconColor(error, warning, disabled) {
    if (disabled) {
        return colors.content.onSurfaceDisabled;
    }
    else if (error) {
        return colors.content.onSurfaceError;
    }
    else if (warning) {
        return colors.content.onSurfaceWarning;
    }
    else {
        return colors.content.onSurface;
    }
}
const Label = styled.label `
  font-family: ${fonts.regular};
  pointer-events: none;
  position: absolute;
  transition: ${ANIMATION_DURATION} ease all;
  color: ${({ showError, showWarning }) => getLabelColor(showError, showWarning)};
  font-feature-settings: 'ss02' on, 'ss09' on, 'zero' on;
  ${TYPOGRAPHY.body2.desktop}
  padding: 18px 12px;
  white-space: normal;
  max-width: 100%;

  ${({ hasContent, inputStyle }) => {
    if (hasContent && inputStyle === 'centered') {
        return `display: none;`;
    }
    else if (hasContent) {
        return getSmallLabelStyles();
    }
}}

  // ellipses if the label is too long for the box
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  overflow-wrap: break-word;
  text-overflow: ellipsis;
`;
const ErrorMessageContainer = styled.div `
  & > * {
    max-width: 100%;
    overflow-wrap: break-word;
    text-overflow: ellipsis;
    white-space: normal;
    word-wrap: break-word;
  }
  ${({ inputStyle }) => {
    if (inputStyle === 'centered') {
        return `
        display: flex;
        justify-content: center;
        margin-top: 4px;
        text-align: center;
    `;
    }
}}
`;
const InputWrapper = styled.div `
  display: flex;
  flex-direction: column;
  position: relative;
  justify-content: flex-start;
  ${({ isDisabled }) => isDisabled &&
    `
    pointer-events: none;
    ${Input} {
      border-color: transparent;
      background: ${colors.surface.surfaceMid};
      color: ${colors.content.onSurfaceDisabled};
    }
    ${Label} {
      color: ${colors.content.onSurfaceDisabled};
    }
  `}

  ${({ inputStyle }) => {
    if (inputStyle === 'centered') {
        return `
      text-align: center;
      align-items: center;

      ${Input} {
        text-align: center;
        padding: 15px 12px;
      }
      `;
    }
}}

  ${Input}:focus + ${Label} {
    display: ${({ inputStyle }) => inputStyle === 'centered' ? 'none' : 'block'};
    ${getSmallLabelStyles()}
  }
`;
const TrailingIconWrapper = styled.div `
  & > * {
    padding: 12px;
    display: flex;
    align-items: center;
    position: absolute;
    // account for input field border
    right: 1px;
    top: 5px;
  }
`;
/**
 *
 * @param prefix
 * @returns
 */
function ImpInput({ inputStyle = 'left', isDisabled = false, isSelect = false, id = 'input', isDropdownShown = false, autocomplete = 'off', showError = false, showWarning = false, autofocus = false, maxWidth, numeric = false, label, value = '', errorMessage, type, maxLength, mask, trailingIcon, // TODO: [TD] try to deprecate if all icons are tinted
trailingIconWithColor, focusContent, caption, inputRef, instrumentation, onChange, onBlur, onFocus, onKeyDown, onEnter, prefix = '', beforeChange, readOnly, }) {
    const [instrumentationFired, setInstrumentationFired] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const componentInputRef = useRef();
    const runInstrumentation = () => {
        if (value !== '' && !instrumentationFired) {
            instrumentation === null || instrumentation === void 0 ? void 0 : instrumentation();
            setInstrumentationFired(true);
        }
    };
    useEffect(() => {
        runInstrumentation();
    }, [value]);
    useEffect(() => {
        if (autofocus && (componentInputRef === null || componentInputRef === void 0 ? void 0 : componentInputRef.current)) {
            componentInputRef.current.focus();
        }
        else if (autofocus && (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current)) {
            inputRef.current.focus();
        }
    }, [autofocus]);
    const handleOnChange = (e) => {
        beforeChange && beforeChange(e);
        let val = e.target.value;
        if (val.length < prefix.length)
            val = prefix; // cannot be shorter than prefix
        if (val.startsWith(prefix))
            val = val.slice(prefix.length); // remove prefix from start
        if (numeric) {
            // only allow parentheses, hyphens, spaces, and numbers
            val = val.replace(/[^0-9( )-]/g, '');
        }
        // If maxLength is defined and value exceeds it, return early without updating state
        if (maxLength && val.length > maxLength) {
            val = val.slice(0, maxLength);
        }
        runInstrumentation();
        const newEvent = { ...e, target: { ...e.target, value: val } };
        onChange === null || onChange === void 0 ? void 0 : onChange(newEvent);
    };
    const handleOnFocus = (e) => {
        setIsFocused(true);
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    };
    const handleOnBlur = (e) => {
        setIsFocused(false);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
    };
    const handleOnKeyDown = (e) => {
        if (onEnter && e.key === 'Enter') {
            e.preventDefault();
            onEnter();
            return;
        }
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
    };
    const getType = () => {
        if (type) {
            return type;
        }
        else if (mask) {
            return 'password';
        }
        else if (numeric) {
            return 'tel';
        }
        return 'text';
    };
    const hasContent = (value === null || value === void 0 ? void 0 : value.length) > 0;
    return (React.createElement(ImpContainer, { dataTestId: "input-container", cursor: isDisabled ? 'not-allowed' : 'auto', width: "100%", maxWidth: maxWidth || `min(100%, ${MaxWidth.Input})` },
        React.createElement(InputWrapper, { isDisabled: isDisabled, inputStyle: inputStyle, "data-testid": "input-wrapper" },
            React.createElement(Input, { id: id, ref: inputRef || componentInputRef, value: !value && !isFocused ? value : prefix + value, onChange: handleOnChange, onFocus: handleOnFocus, onBlur: handleOnBlur, onKeyDown: handleOnKeyDown, showError: showError, showWarning: showWarning, isDisabled: isDisabled, isSelect: isSelect, readOnly: readOnly !== null && readOnly !== void 0 ? readOnly : isSelect, disabled: isDisabled, type: getType(), maxLength: maxLength, isDropdownShown: isDropdownShown, "data-testid": "input-field", autoComplete: autocomplete, autoFocus: autofocus, placeholder: '' }),
            React.createElement(Label, { htmlFor: id, hasContent: hasContent, showError: showError, showWarning: showWarning, inputStyle: inputStyle }, label),
            trailingIcon && (React.createElement(TrailingIconWrapper, { "data-testid": "input-trailing-icon" }, trailingIcon)),
            trailingIconWithColor && (React.createElement(TrailingIconWrapper, { "data-testid": "input-trailing-icon" }, trailingIconWithColor(getTrailingIconColor(showError, showWarning, isDisabled))))),
        (showError || showWarning) && errorMessage && !isDisabled && (React.createElement(ErrorMessageContainer, { inputStyle: inputStyle },
            React.createElement(ImpSpacer, { height: 'xs' }),
            React.createElement(ImpText, { typography: 'body3', color: showError
                    ? colors.content.onSurfaceError
                    : colors.content.onSurfaceWarning, "data-testid": "input-label" }, errorMessage))),
        caption && (React.createElement(React.Fragment, null,
            React.createElement(ImpSpacer, { height: 'xs' }),
            React.createElement(ImpText, { typography: 'body3', color: colors.content.onSurfaceVariant, "data-testid": "input-label" }, caption))),
        focusContent && isFocused && (React.createElement(React.Fragment, null,
            React.createElement(ImpSpacer, { height: 'xs' }),
            focusContent))));
}
export default ImpInput;
//# sourceMappingURL=ImpInput.js.map